/** Indica cancelamento por AbortController / axios (request substituída ou timeout). */
export function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const anyErr = err as { name?: string; code?: string; message?: string };
  if (anyErr.name === 'AbortError' || anyErr.name === 'CanceledError') return true;
  if (anyErr.code === 'ERR_CANCELED') return true;
  if (typeof anyErr.message === 'string' && anyErr.message.toLowerCase().includes('aborted')) {
    return true;
  }
  return false;
}

/**
 * Extrai mensagem legível de erros HTTP (Axios / ASP.NET ProblemDetails).
 */

/** Exibida quando a requisição excede o tempo limite configurado no Axios (ECONNABORTED). */
export const TIMEOUT_ERROR_MESSAGE = 'A operação demorou mais que o esperado. Tente novamente.';

/** Exibida quando a requisição foi enviada mas não houve resposta do servidor (falha real de rede). */
export const NETWORK_ERROR_MESSAGE = 'Falha de conexão com o servidor. Verifique sua internet e tente novamente.';

interface TransportErrorShape {
  code?: string;
  name?: string;
  message?: string;
  request?: unknown;
  response?: unknown;
  __CANCEL__?: boolean;
}

/**
 * Requisição cancelada intencionalmente (AbortController/CancelToken próprio).
 * Não representa uma falha real — quem chamar deve evitar exibir qualquer erro ao usuário.
 */
export function isCanceledError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as TransportErrorShape;
  return (
    e.code === 'ERR_CANCELED' ||
    e.name === 'CanceledError' ||
    e.name === 'AbortError' ||
    e.__CANCEL__ === true
  );
}

/** Requisição abortada por exceder o timeout configurado no Axios (ECONNABORTED). */
export function isTimeoutError(err: unknown): boolean {
  if (!err || typeof err !== 'object' || isCanceledError(err)) return false;
  const e = err as TransportErrorShape;
  if (e.code === 'ECONNABORTED') return true;
  return typeof e.message === 'string' && /timeout of .+ exceeded/i.test(e.message);
}

/**
 * Falha real de rede: a requisição foi enviada (`request` existe) mas nenhuma resposta chegou
 * (`response` ausente), e não é timeout nem cancelamento.
 */
export function isNetworkError(err: unknown): boolean {
  if (!err || typeof err !== 'object' || isCanceledError(err) || isTimeoutError(err)) return false;
  const e = err as TransportErrorShape;
  return !!e.request && !e.response;
}

const GENERIC_VALIDATION_TITLES = new Set([
  'one or more validation errors occurred.',
  'one or more validation errors occurred',
  'bad request',
  'validation failed',
  'dados de entrada inválidos.',
  'dados de entrada inválidos',
]);

function isGenericValidationTitle(value: string): boolean {
  return GENERIC_VALIDATION_TITLES.has(value.trim().toLowerCase());
}

function collectValidationErrors(errors: Record<string, unknown>): string[] {
  const validationErrors: string[] = [];

  Object.values(errors).forEach((fieldErrors) => {
    if (Array.isArray(fieldErrors)) {
      fieldErrors.forEach((item) => {
        if (typeof item === 'string' && item.trim()) {
          validationErrors.push(item.trim());
        }
      });
      return;
    }

    if (typeof fieldErrors === 'string' && fieldErrors.trim()) {
      validationErrors.push(fieldErrors.trim());
    }
  });

  return validationErrors;
}

export function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (isTimeoutError(err)) return TIMEOUT_ERROR_MESSAGE;
  if (isNetworkError(err)) return NETWORK_ERROR_MESSAGE;

  const anyErr = err as {
    response?: { data?: unknown };
    message?: string;
  };

  if (anyErr?.response?.data) {
    const data = anyErr.response.data;

    if (typeof data === 'string' && data.trim()) {
      return data.trim();
    }

    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;

      if (obj.errors && typeof obj.errors === 'object' && obj.errors !== null) {
        const validationErrors = collectValidationErrors(obj.errors as Record<string, unknown>);
        if (validationErrors.length > 0) {
          return validationErrors.join(' ');
        }
      }

      if (typeof obj.message === 'string' && obj.message.trim() && !isGenericValidationTitle(obj.message)) {
        return obj.message.trim();
      }

      if (typeof obj.detail === 'string' && obj.detail.trim()) {
        return obj.detail.trim();
      }

      if (typeof obj.title === 'string' && obj.title.trim() && !isGenericValidationTitle(obj.title)) {
        return obj.title.trim();
      }

      if (Array.isArray(data)) {
        const items = data
          .map((item) => {
            if (typeof item === 'string') return item;
            if (item && typeof item === 'object') {
              const o = item as Record<string, unknown>;
              if (typeof o.message === 'string') return o.message;
              if (typeof o.title === 'string' && !isGenericValidationTitle(o.title)) return o.title;
            }
            return null;
          })
          .filter((s): s is string => !!s && s.trim().length > 0);
        if (items.length > 0) return items.join(' ');
      }
    }
  }

  if (anyErr?.message && typeof anyErr.message === 'string' && anyErr.message.trim()) {
    const message = anyErr.message.trim();
    if (!isGenericValidationTitle(message)) {
      return message;
    }
  }

  if (isAbortError(err)) {
    return 'A requisição demorou demais ou foi cancelada. Tente novamente.';
  }

  return fallback;
}

/**
 * Executa uma requisição com AbortSignal e timeout explícito.
 */
export async function withRequestTimeout<T>(
  run: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  timeoutMessage: string,
  externalSignal?: AbortSignal
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const onExternalAbort = () => controller.abort();
  externalSignal?.addEventListener('abort', onExternalAbort);

  try {
    return await run(controller.signal);
  } catch (err) {
    if (controller.signal.aborted && timedOut) {
      throw new Error(timeoutMessage);
    }
    throw err;
  } finally {
    window.clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', onExternalAbort);
  }
}
